import _ from 'lodash';
import Joi from 'joi';
import Datepicker from "react-tailwindcss-datepicker";
import dayjs from 'dayjs';
import config from '../../config.js';

import {
  PhotoIcon
} from '@heroicons/react/24/solid';

import {
  Form,
  useNavigate,
  redirect,
  useActionData,
  json,
  useLoaderData,
  Link
} from "react-router-dom";

import {
  useEffect,
  useState
} from 'react';

import {
  DEFAULT_INCOME_TAXONOMY_URL,
  DEFAULT_EXPENSE_TAXONOMY_URL
} from '../../categories';

import {
  fetchClient
} from '../../util/fetchClient.js';

import RedExclamationCircle from '../_Shared/RedExclamationCircle';

import {
  useAuth
} from '../../hooks/useAuth.js';

const MAX_FILE_SIZE = 5; // in MB

const ledgerEntrySchema = Joi.object({
  id: Joi.number().integer().optional(),
  type: Joi.string().valid('income','expense').required(),
  description: Joi.string().allow(null, ''),
  amount: Joi.number().min(0.1).required(),
  currency: Joi.string().valid('mxn').required(),
  invoice_id: Joi.string().optional().allow('', null),
  category_id: Joi.number().integer().min(1).required(),
  payment_method_id: Joi.number().integer().min(1).required(),
  branch_id: Joi.number().integer().min(1).required(),
  created_at: Joi.date().required(),
  file_url: Joi.string().optional().allow(null, ''),
}).options({ abortEarly: false });

const getRequestBody = (data) => ({
  id: parseInt(data.id) || undefined,
  type: data.type,
  description: data.description,
  amount: +(parseFloat(data.amount) * 100).toFixed(), // stored in cents
  currency: 'mxn',
  invoice_id: data.invoice_id,
  category_id: parseInt(data.category_id),
  payment_method_id: parseInt(data.payment_method_id),
  branch_id: parseInt(data.branch_id),
  created_at: data.created_at,
  file_url: data.selected_file
});

const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ')
};

const loadLedgerEntry = async (id) => {
  const { body: ledgerEntry } = await fetchClient(`/v1/ledger/${id}`);
  return ledgerEntry;
};

const loadBranches = async () => {
  const { body: branches } = await fetchClient('/v1/branches');
  return branches?.data || [];
};

const fetchCategories = async (categoriesURL) => {
  const { body: categories } = await fetchClient('', { credentials: 'same-origin' }, categoriesURL);
  return { categories };
};

const loadPaymentMethods = async () => {
  const { body: paymentMethods } = await fetchClient('/v1/payment_methods');

  return paymentMethods?.data || [];
};

const getTaxonomy = (categories) => _.reduce(
  categories.taxonomy,
  (memo, value, key) => {
    memo[categories[key].name] = value.map(
      (subCatId) => ({ id: categories[subCatId].id, name: categories[subCatId].name })
    );

    return memo;
  }, {});

const getPresignedUrl = async (contentType) => {
  const response = await fetchClient(`/v1/files/upload?content_type=${contentType}`, { method: 'GET' });

  if (response.response.ok) {
    return response.body;
  } else {
    return {};
  }
};

const uploadImageToS3 = async (file) => {
  const contentType = file.type;
  const { uploadUrl, key } = await getPresignedUrl(contentType);

  if (!uploadUrl) {
    console.log('Failed to get presigned URL');
    return;
  }

  const response = await fetch(
    uploadUrl,
    {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': contentType, 'Content-Length': file.size },
      body: file
  });

  if (response.ok) {
    return `${config.roenFilesURL}/${key}`;
  } else {
    console.log('Failed to upload file to S3');
    return;
  }
};

export const loader = async ({ params }) => {
  const response = {};
  if (params.id) {
    response.ledgerEntry = await loadLedgerEntry(params.id);
  } else {
    response.ledgerEntry = { type: 'income' };
  }

  return response;
};

export const action = async ({ request }) => {
  const data = Object.fromEntries(await request.formData());
  const body = getRequestBody(data);

  try {
    await ledgerEntrySchema.validateAsync(body, { abortEarly: false });
  } catch (validationError) {
    console.log(`validationError`, validationError, body);
    const errors = {};
    validationError?.details.map((details) => errors[details.context.key] = details.message);
    return json({ errors });
  }

  let response;
  switch(request.method) {
    case 'POST':
      response = await fetchClient('/v1/ledger', { method: 'POST', body: JSON.stringify(body) });
      break;
    case 'PUT':
      response = await fetchClient(`/v1/ledger/${body.id}`, { method: 'PUT', body: JSON.stringify(body) });
      break;
    default:
  }

  if (response?.response?.ok) {
    return redirect('/ledger');
  } else {
    const message = `ERROR: Server failed to process the request(${response?.status})`;
    console.log(message);
    return json({ errors: { api : message } });
  }
};

const formatDate = date => dayjs(date).format('DD/MM/YYYY');

export default function LedgerForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cancelButtonClick = () => navigate('/ledger');
  const actionData = useActionData();
  const loaderData = useLoaderData();
  const [entryType, setEntryType] = useState(loaderData?.ledgerEntry?.type);
  const [incomeCategories, setIncomeCategories] = useState({});
  const [expenseCategories, setExpenseCategories] = useState({});
  const [categories, setCategories] = useState({});
  const [selectedFile, setSelectedFile] = useState(loaderData?.ledgerEntry?.file_url);
  const [fileError, setFileError] = useState(false);
  const [branches, setBranches] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function getBranches() {
      if (!ignore) {
        const branches = await loadBranches();
        setBranches(branches);
      }
    }

    let ignore = false;
    getBranches();
    return () => {
      ignore = true;
    }
  }, []);

  useEffect(() => {
    async function getPaymentMethods() {
      if (!ignore) {
        const paymentMethods = await loadPaymentMethods();
        setPaymentMethods(paymentMethods);
      }
    }

    let ignore = false;
    getPaymentMethods();
    return () => {
      ignore = true;
    }
  }, []);

  useEffect(() => {
    async function getCategories() {
      if (!ignore) {
        const { categories: newIncomeCategories } = await fetchCategories(user.income_taxonomy_url || DEFAULT_INCOME_TAXONOMY_URL);
        setIncomeCategories(newIncomeCategories);

        const { categories: newExpenseCategories } = await fetchCategories(user.expense_taxonomy_url || DEFAULT_EXPENSE_TAXONOMY_URL);
        setExpenseCategories(newExpenseCategories);

        if (loaderData?.ledgerEntry?.type === 'income') {
          setCategories(getTaxonomy(newIncomeCategories))
        } else {
          setCategories(getTaxonomy(newExpenseCategories))
        }
      }
    }

    let ignore = false;
    getCategories();
    return () => {
      ignore = true;
    }
  }, [loaderData?.ledgerEntry?.type, user.income_taxonomy_url, user.expense_taxonomy_url]);

  const handleDownload = async () => {
    const response = await fetch(
      `${config.roenBackendURL}/v1/files/download?url=${selectedFile}`,
      { method: 'GET', credentials: 'include' }
    );
    const fileName = new URL(selectedFile).pathname.split('/').pop();
    const blob = await response.blob();
    const fileURL = window.URL.createObjectURL(blob);
    const alink = document.createElement('a');
    alink.href = fileURL;
    alink.download = fileName;
    alink.click();
  };

  const onFileChange = async (event) => {
    const file = event.target.files[0];

    if (file && ((file.size / 1024 / 1024).toFixed(2) <= MAX_FILE_SIZE)) {
      const fileUrl = await uploadImageToS3(file);
      setFileError(false);
      setSelectedFile(fileUrl);
    } else {
      setSelectedFile(selectedFile);
      setFileError(true);
    }
  };

  const renderUploadFileSection = () => {
    if (selectedFile) {
        return (
            <div className="justify-center">
              <div className="text-gray-600">
                <embed src={selectedFile} width="300" height="200"/>
              </div>
            </div>
        );
    } else {
        return (
          <div>
            <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
          </div>
        );
    }
};

  const [createdAt, setCreatedAt] = useState({ startDate: dayjs(loaderData?.ledgerEntry?.created_at), endDate: null });

  const handleCreatedAtChange = (newCreatedAt) => {
    setCreatedAt(newCreatedAt);
  };

  const typeClickHandler = (type) => {
    const categories = type === 'income' ? getTaxonomy(incomeCategories) : getTaxonomy(expenseCategories);
    setCategories(categories);
    setEntryType(type);
  };

  const handleSubmit = (event) => {
    setIsSubmitting(true);
    return event;
  };

  return (
    <Form onSubmit={handleSubmit} method={loaderData?.ledgerEntry?.id ? "put" : "post"} encType="multipart/form-data">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">Información del Ingreso/Egreso</h2>

            <input hidden id="id" name="id" defaultValue={loaderData?.ledgerEntry?.id} />
            <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-6">
                <label htmlFor="type" className="block text-sm font-medium leading-6 text-gray-900">
                  Tipo
                </label>
                <div className="mt-2 space-y-6">
                  <div className="flex items-center space-x-1 rounded-lg bg-slate-100 px-0.5 w-44 py-1"
                    role="tablist"
                    aria-orientation="horizontal"
                  >
                    <input
                      id="income"
                      name="type"
                      value="income"
                      type="radio"
                      onClick={() => typeClickHandler('income')}
                      defaultChecked={entryType === 'income'}
                      className='hidden'
                    />
                    <label htmlFor="income" className={classNames(
                      entryType === 'income' ? 'bg-white shadow text-gray-900' : 'text-gray-600',
                      "flex items-center rounded-md py-1.5 px-2 text-sm font-medium hover:cursor-pointer"
                    )}>
                      <span class="px-2 text-text-gray-900">Ingreso</span>
                    </label>

                    <input
                      id="expense"
                      name="type"
                      value="expense"
                      type="radio"
                      onClick={() => typeClickHandler('expense')}
                      defaultChecked={entryType === 'expense'}
                      className='hidden'
                    />
                    <label htmlFor="expense" className={classNames(
                      entryType === 'expense' ? 'bg-white shadow text-gray-900' : 'text-gray-600',
                      "flex items-center rounded-md py-1.5 px-2 text-sm font-medium hover:cursor-pointer"
                    )}>
                      <span class="px-2">Egreso</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="created-at"
                  className={actionData?.errors && actionData.errors.created_at
                    ? "block text-sm font-medium leading-6 text-rose-600"
                    : "block text-sm font-medium leading-6 text-gray-900"
                  }>
                  Fecha de registro
                </label>
                <div className="mt-2">
                  <input
                    type="hidden"
                    name="created_at"
                    id="created-at"
                    value={dayjs(createdAt.startDate).format('YYYY-MM-DD HH:mm:ssZ')}
                  />
                  <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                    <Datepicker
                      i18n={"es-mx"}
                      primaryColor={"indigo"}
                      asSingle={true}
                      useRange={false}
                      readOnly={true}
                      displayFormat={"DD/MM/YYYY"}
                      placeholder={formatDate(loaderData?.ledgerEntry?.created_at)}
                      value={createdAt}
                      onChange={handleCreatedAtChange}
                    />
                  </div>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="branch" className="block text-sm font-medium leading-6 text-gray-900">
                  Sucursal
                </label>
                <div className="mt-2">
                  <select
                    id="branch"
                    name="branch_id"
                    defaultValue={loaderData?.ledgerEntry?.branch_id}
                    className={actionData?.errors && actionData.errors.branch_id
                      ? "block w-full rounded-md border-rose-600 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:max-w-xs sm:text-sm sm:leading-6"
                      : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    }
                  >
                    {
                      branches && branches.map((branch) => (<option value={branch.id} selected={String(branch.id) === String(loaderData?.ledgerEntry?.branch_id)}>{branch.name}</option>))
                    }
                  </select>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="invoice-id" className="block text-sm font-medium leading-6 text-gray-900">
                  Folio
                </label>
                <div className="mt-2">
                  <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                    <input
                      type="text"
                      name="invoice_id"
                      id="invoice-id"
                      defaultValue={loaderData?.ledgerEntry?.invoice_id}
                      className={
                        actionData?.errors && actionData.errors.invoice_id
                          ? "block flex-1 border-rose-600 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                          : "block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="category" className="block text-sm font-medium leading-6 text-gray-900">
                  Categoría
                </label>
                <div className="mt-2">
                  <select
                    id="category"
                    name="category_id"
                    defaultValue={loaderData?.ledgerEntry?.category_id}
                    className={
                      actionData?.errors && actionData.errors.category_id
                        ? "block w-full rounded-md border-rose-600 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                        : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    }
                  >
                    {
                      categories && _.keys(categories).map(
                        (parentCategory) => (
                          <optgroup label={parentCategory}>
                            {
                              categories[parentCategory].map(
                                (category) => (
                                  <option value={category.id} selected={String(category.id) === String(loaderData?.ledgerEntry?.category_id)}>{category.name}</option>
                                ))
                            }
                          </optgroup>
                        ))
                    }
                  </select>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="amount" className="block text-sm font-medium leading-6 text-gray-900">
                  Monto
                </label>
                <div className="relative mt-2">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                    <input
                      type="text"
                      name="amount"
                      id="amount"
                      defaultValue={loaderData?.ledgerEntry?.amount}
                      aria-describedby="price-currency"
                      className={
                        actionData?.errors && actionData.errors.amount
                          ? "block flex-1 rounded-md border-rose-600 bg-transparent py-1.5 pl-7 text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:ring-inset focus:ring-rose-6000 sm:text-sm sm:leading-6"
                          : "block w-full rounded-md border-0 py-1.5 pl-7 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label htmlFor="payment-method" className="block text-sm font-medium leading-6 text-gray-900">
                  Método de pago
                </label>
                <div className="mt-2">
                  <select
                    id="payment-method"
                    name="payment_method_id"
                    defaultValue={loaderData?.ledgerEntry?.payment_method_id}
                    className={
                      actionData?.errors && actionData.errors.payment_method_id
                        ? "block w-full rounded-md border-rose-600 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                        : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    }
                  >
                    {
                      paymentMethods && paymentMethods.map((method) => (<option value={method.id}  selected={String(method.id) === String(loaderData?.ledgerEntry?.payment_method_id)}>{method.name}</option>))
                    }
                  </select>
                </div>
              </div>

              <div className="col-span-full">
                <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
                  Descripción
                </label>
                <div className="mt-2">
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    defaultValue={loaderData?.ledgerEntry?.description}
                    className={
                      actionData?.errors && actionData.errors.description
                        ? "block w-full rounded-md border-rose-600 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        : "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    }
                  />
                </div>
              </div>

              <input type="hidden" value={selectedFile} name="selected_file" id="selected-file" />
              <div className="col-span-full">
                <label htmlFor="file-upload" className={fileError
                  ? "block text-sm font-medium leading-6 text-rose-600"
                  : "block text-sm font-medium leading-6 text-gray-900"
                }>
                  Archivo (png, jpg, pdf 5mb max)
                </label>
                <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                  <div>
                    {renderUploadFileSection()}
                    <div className="mt-4 flex justify-center text-sm leading-6 text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                      >
                        <span>Subir un archivo</span>
                        <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={onFileChange} />
                      </label>
                    </div>
                  </div>
                </div>
                {selectedFile
                  && (<div className="mt-4 flex text-sm leading-6 font-semibold text-indigo-600">
                    <Link onClick={handleDownload}>Descargar archivo</Link>
                  </div>
                  )
                }
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-x-6">
          <button type="button" onClick={cancelButtonClick} className="text-sm font-semibold leading-6 text-gray-900">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </button>
          {actionData?.errors && actionData.errors ? <RedExclamationCircle /> : null}
        </div>
      </div>
    </Form>
  )
}
